// FO-SOI-3: thin EventSource wrapper for the /api/sales-orders/events SSE stream.
// Mirrors features/quotations/lib/quotationEventStream.ts (CSC-297) — exponential
// backoff reconnect, JWT via ?access_token= query param (the BE's
// JwtBearerEvents.OnMessageReceived accepts it for the /api/sales-orders/events
// path per FO-SOI-1's Program.cs edit).

import { tokenService } from '@features/authentication/services/tokenService';
import { ensureFreshAccessTokenForStream } from '@shared/api/ensureFreshAccessTokenForStream';
import type { SalesOrderEvent } from '../types/salesOrder.types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;
const STREAM_PATH = '/sales-orders/events';
const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

export type ConnectionState = 'connecting' | 'open' | 'reconnecting';

export interface SalesOrderEventStreamHandlers {
  onEvent: (evt: SalesOrderEvent) => void;
  onConnectionStateChange?: (state: ConnectionState) => void;
  onReconnect?: () => void;
}

export interface SalesOrderEventStreamHandle {
  close: () => void;
}

export function openSalesOrderEventStream(
  handlers: SalesOrderEventStreamHandlers,
): SalesOrderEventStreamHandle {
  let source: EventSource | null = null;
  let backoff = MIN_BACKOFF_MS;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  let hasConnectedOnce = false;

  function emitState(next: ConnectionState) {
    handlers.onConnectionStateChange?.(next);
  }

  function buildUrl(): string {
    const accessToken = tokenService.getAccessToken();
    const params = new URLSearchParams();
    if (accessToken) params.set('access_token', accessToken);
    return `${API_BASE_URL}${STREAM_PATH}?${params.toString()}`;
  }

  function connect() {
    if (closed) return;
    emitState(hasConnectedOnce ? 'reconnecting' : 'connecting');

    try {
      source = new EventSource(buildUrl());
    } catch {
      scheduleReconnect();
      return;
    }

    source.onopen = () => {
      backoff = MIN_BACKOFF_MS;
      emitState('open');
      if (hasConnectedOnce) handlers.onReconnect?.();
      hasConnectedOnce = true;
    };

    source.onmessage = (msg) => {
      if (!msg.data) return;
      try {
        const evt = JSON.parse(msg.data) as SalesOrderEvent;
        handlers.onEvent(evt);
      } catch (err) {
        console.warn('Failed to parse sales-order event payload:', err, msg.data);
      }
    };

    source.onerror = () => {
      source?.close();
      source = null;
      scheduleReconnect();
    };
  }

  function scheduleReconnect() {
    if (closed) return;
    emitState('reconnecting');
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(async () => {
      reconnectTimer = null;
      // E7 item 4 — refresh before reconnecting; see quotationEventStream for the full rationale.
      await ensureFreshAccessTokenForStream();
      if (closed) return;
      connect();
    }, backoff);
    backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
  }

  connect();

  return {
    close() {
      closed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (source) {
        source.close();
        source = null;
      }
    },
  };
}
